import React, { useState, useEffect, useRef, useMemo } from 'react'
import { useChatContext } from 'stream-chat-react'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import { useGetAdvisorDashboardQuery } from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'

import { uploadFiles } from 'components/AppFileUploader/AppFileUploader.helper'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'

interface GroupCreationProps {
	onClose: () => void
	onGroupCreated: (channel: any) => void
	chatUsers: any
}

const GroupCreation: React.FC<GroupCreationProps> = ({ onClose, onGroupCreated, chatUsers }) => {
	const [groupName, setGroupName] = useState('')
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
	const [users, setUsers] = useState<IAssigneeOptions[]>([])
	const [isCreating, setIsCreating] = useState(false)
	const [groupIcon, setGroupIcon] = useState<string | null>(null)
	const fileInputRef = useRef<HTMLInputElement>(null)

	const { client } = useChatContext()
	useUserInfo()
	const tenantId = useTenantId()
	const loggedInUser = useSelector((state: RootState) => state.user.user)
	const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)

	// Get business profile to access team members
	const { data: advisorData } = useGetAdvisorDashboardQuery({ tenantId })
	const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

	// Memoize assignee options to prevent infinite re-renders
	const assigneeOptions = useMemo(() => {
		let options: IAssigneeOptions[] = []
		
		if (!isAdvisor) {
			options = businessAssigneeOptions
		} else {
			const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
			options =
				response?.accounts?.map((account: any) => ({
					label: `${account?.firstName} ${account?.lastName}`,
					value: account?.id,
					user: {
						id: account?.id,
						firstName: account?.firstName,
						lastName: account?.lastName,
					},
				})) || []
		}

		// Filter out current user
		return options.filter((option) => option.value !== loggedInUser?.id)
	}, [isAdvisor, businessAssigneeOptions, advisorData, loggedInUser?.id])

	useEffect(() => {
		setUsers(assigneeOptions)
	}, [assigneeOptions])

	const filteredUsers = users.filter((user) =>
		user.label.toLowerCase().includes(searchQuery.toLowerCase())
	)



	const handleUserToggle = (userId: string) => {
		const newSelected = new Set(selectedUsers)
		if (newSelected.has(userId)) {
			newSelected.delete(userId)
		} else {
			newSelected.add(userId)
		}
		setSelectedUsers(newSelected)
	}

	const handleIconClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (file) {
			// Upload to S3 and store the resulting URL for Stream channel image
			try {
				const uploaded = await uploadFiles({
					files: [file],
					tenantId,
					clientId: '',
					userId: loggedInUser?.id,
				})
				setGroupIcon(uploaded?.[0]?.url || null)
			} catch (err) {
				console.error('Failed to upload group icon', err)
			}
		}
	}

	const handleCreateGroup = async () => {
		if (!groupName.trim() || selectedUsers.size === 0) return

		setIsCreating(true)
		try {
			const selectedUsersIds = Array.from(selectedUsers)
			
			const customData: any = {
				members: [...selectedUsersIds, client.userID || 'unknown'],
				team: tenantId,
				name: groupName.trim(),
			}

			// Add image only if it exists
			if (groupIcon) {
				customData.image = groupIcon
			}

			// Create channel with name and image metadata
			const channel = client.channel('messaging', customData)
			await channel.create()

			// Watch the channel
			await channel.watch()

			onGroupCreated(channel)
		} catch (error) {
			console.error('Failed to create group:', error)
		} finally {
			setIsCreating(false)
		}
	}



	return (
		<div className="fixed inset-0 bg-app-black-50 flex items-center justify-center z-50 p-4">
			<div className="bg-app-white-100 rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
				{/* Header */}
				<div className="px-6 py-4 border-b border-app-grey-25 flex items-center justify-between">
					<h2 className="text-xl font-semibold text-app-black-100">New group</h2>
					<button
						onClick={onClose}
						className="p-2 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
					>
						<svg className="w-5 h-5 text-app-grey-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				{/* Group Icon & Name */}
				<div className="px-6 py-6 border-b border-app-grey-25">
					<h3 className="text-sm font-medium text-app-grey-70 mb-4">Group Icon & Name</h3>
					<div className="flex items-center gap-4">
						<div 
							className="w-16 h-16 rounded-full flex items-center justify-center bg-app-grey-20 border-2 border-dashed border-app-grey-40 cursor-pointer hover:bg-app-grey-10 transition-colors duration-150 overflow-hidden"
							onClick={handleIconClick}
						>
							{groupIcon ? (
								<img 
									src={groupIcon} 
									alt="Group icon" 
									className="w-full h-full object-cover rounded-full"
								/>
							) : (
								<svg className="w-8 h-8 text-app-grey-55" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
									/>
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
							)}
						</div>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							onChange={handleFileChange}
							className="hidden"
						/>
						<div className="flex-1">
							<input
								type="text"
								placeholder="Group name"
								value={groupName}
								onChange={(e) => setGroupName(e.target.value)}
								className="w-full px-4 py-2.5 border border-app-grey-25 rounded-lg text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
							/>
						</div>
					</div>
				</div>

				{/* Add Members */}
				<div className="px-6 py-4 border-b border-app-grey-25">
					<h3 className="text-sm font-medium text-app-grey-70 mb-4">Add members</h3>
					<div className="relative mb-4">
						<svg
							className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-app-grey-55"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth="2"
								d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
							/>
						</svg>
						<input
							type="text"
							placeholder="Search name...."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full pl-10 pr-4 py-2.5 bg-app-grey-20 border border-app-grey-25 rounded-lg text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
						/>
					</div>
				</div>

				{/* Members List */}
				<div className="flex-1 overflow-y-auto max-h-64">
					<div className="px-6">
						{filteredUsers.map((user) => (
							<div
								key={user.value}
								className="flex items-center gap-3 py-3 cursor-pointer hover:bg-app-grey-10 rounded-lg px-2"
								onClick={() => handleUserToggle(user.value)}
							>
															<AppAvatar
								size="md"
								type="mixed"
								shape="Rounded"
								text={getInitials({
									firstName: user.user?.firstName || '',
									lastName: user.user?.lastName || '',
								})}
							/>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-app-black-100 truncate">{user.label}</p>
								</div>
								<div
									className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
										selectedUsers.has(user.value)
											? 'bg-app-primary-60 border-app-primary-60'
											: 'border-app-grey-30 bg-white'
									}`}
								>
									{selectedUsers.has(user.value) && (
										<svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
										</svg>
									)}
								</div>
							</div>
						))}
					</div>
				</div>

				{/* Footer */}
				<div className="px-6 py-4 border-t border-app-grey-25">
					<button
						onClick={handleCreateGroup}
						disabled={!groupName.trim() || selectedUsers.size === 0 || isCreating}
						className={`w-full py-2.5 px-4 rounded-lg font-medium transition-colors duration-150 ${
							!groupName.trim() || selectedUsers.size === 0 || isCreating
								? 'bg-app-grey-20 text-app-grey-55 cursor-not-allowed'
								: 'bg-app-primary-60 text-app-white-100 hover:bg-app-primary-70'
						}`}
					>
						{isCreating ? 'Creating group...' : 'Create group'}
					</button>
				</div>
			</div>
		</div>
	)
}

export default GroupCreation 